/**
 * LookupSuggestionDropdown — Floating list of slash-command suggestions.
 *
 * Designed to be absolutely positioned relative to the input box container
 * (e.g. anchored at `bottom-full` so it floats above the textarea). It is purely
 * presentational: parents pass in the ranked matches plus highlight/select
 * callbacks produced by `useSlashLookup`.
 *
 * Up to ~10 rows are shown at once; additional matches (the hook returns up to
 * 30 by default) are reachable by scrolling. The highlighted row is auto-scrolled
 * into view as the user navigates with the arrow keys.
 */

import React, { useEffect, useRef } from 'react';
import { MatchRange, ScoredMatch } from './lookupSearchAlgo';

export interface LookupSuggestionDropdownProps {
    suggestions: ScoredMatch[];
    highlightIndex: number;
    onHighlight: (index: number) => void;
    onSelect: (index: number) => void;
    /** Optional className appended to the outer container for positioning overrides. */
    className?: string;
    'data-test-id'?: string;
}

/** Each row is `py-2` (16px) + a 20px line, giving ~36px per row.
 *  10 rows × 36px + 8px container `py-1` ≈ 368px. */
const DROPDOWN_MAX_HEIGHT = 368;

function renderHighlighted(text: string, ranges: MatchRange[]): React.ReactNode {
    if (!ranges || ranges.length === 0) return text;
    const parts: React.ReactNode[] = [];
    let cursor = 0;
    const sorted = [...ranges].sort((a, b) => a.start - b.start);
    for (let i = 0; i < sorted.length; i++) {
        const r = sorted[i];
        const start = Math.max(r.start, cursor);
        const end = Math.min(r.end, text.length);
        if (start > cursor) {
            parts.push(<span key={`p-${i}`}>{text.slice(cursor, start)}</span>);
        }
        if (end > start) {
            parts.push(
                <span key={`m-${i}`} className="font-semibold text-[#3B2C77] bg-[#f1edff] rounded-sm">
                    {text.slice(start, end)}
                </span>,
            );
            cursor = end;
        }
    }
    if (cursor < text.length) {
        parts.push(<span key="tail">{text.slice(cursor)}</span>);
    }
    return <>{parts}</>;
}

const LookupSuggestionDropdown: React.FC<LookupSuggestionDropdownProps> = ({
    suggestions,
    highlightIndex,
    onHighlight,
    onSelect,
    className,
    'data-test-id': dataTestId,
}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<Array<HTMLDivElement | null>>([]);

    // Key derived from the first suggestion's identity — reset scroll to top whenever
    // the list materially changes (e.g. the user typed a new character and the top
    // match flipped). Using a stable string key avoids spurious resets from array
    // reference churn.
    const firstItemKey = suggestions[0]
        ? `${suggestions[0].item.column_name}::${suggestions[0].item.entity_name}`
        : '';

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTop = 0;
        }
    }, [firstItemKey]);

    useEffect(() => {
        const el = itemRefs.current[highlightIndex];
        if (el) el.scrollIntoView({ block: 'nearest' });
    }, [highlightIndex, firstItemKey]);

    if (!suggestions || suggestions.length === 0) return null;

    return (
        <div
            className={`absolute bottom-full left-0 right-0 mb-2 z-50 bg-white rounded-[12px] border border-[#e8eafa] shadow-[0px_2.5px_20px_-2.5px_rgba(164,99,243,0.32)] overflow-hidden ${
                className ?? ''
            }`}
            role="listbox"
            data-test-id={dataTestId}
            // Prevent the textarea from losing focus when clicking inside the dropdown.
            onMouseDown={(e) => e.preventDefault()}
        >
            <div
                ref={scrollContainerRef}
                className="overflow-y-auto py-1"
                style={{ maxHeight: DROPDOWN_MAX_HEIGHT }}
            >
                {suggestions.map((match, idx) => {
                    const isActive = idx === highlightIndex;
                    return (
                        <div
                            key={`${match.item.column_name}::${match.item.entity_name}::${idx}`}
                            ref={(el) => {
                                itemRefs.current[idx] = el;
                            }}
                            role="option"
                            aria-selected={isActive}
                            className={`flex items-center justify-between gap-3 px-3 py-2 cursor-pointer transition-colors ${
                                isActive ? 'bg-[#f5f3ff]' : 'bg-transparent hover:bg-[#fafafa]'
                            }`}
                            onMouseEnter={() => onHighlight(idx)}
                            onClick={() => onSelect(idx)}
                            data-test-id={
                                dataTestId ? `${dataTestId}-option-${idx}` : undefined
                            }
                        >
                            <span className="font-['Open_Sans'] font-normal text-[14px] leading-5 text-[rgba(0,0,0,0.88)] truncate flex-1 min-w-0">
                                {renderHighlighted(match.item.entity_name, match.ranges)}
                            </span>
                            <span
                                className="font-['Open_Sans'] font-normal text-[12px] leading-4 text-[#3b2c77] bg-[#f1edff] rounded-md px-2 py-0.5 shrink-0 whitespace-nowrap"
                                title={match.item.column_name}
                            >
                                {match.item.column_name}
                            </span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default LookupSuggestionDropdown;
